import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩国漫画网',
  url: 'https://koreancomics.cn',
  description: '免费韩漫大全，提供好看韩国漫画在线观看。覆盖恋爱、奇幻、动作、悬疑等多种类型。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@koreancomics.cn',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '韩国漫画网是免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，韩国漫画网为所有读者提供免费的韩国漫画在线观看服务，无需注册或付费即可畅读海量韩漫作品。',
      },
    },
    {
      '@type': 'Question',
      name: '韩国漫画网有哪些韩漫类型？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们涵盖了恋爱、奇幻、动作、悬疑、搞笑、剧情、恐怖、校园等八大类型，总数超过一万部免费韩漫资源。',
      },
    },
    {
      '@type': 'Question',
      name: '如何反馈问题或提出建议？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过邮箱 contact@koreancomics.cn 或客服QQ 1234567890 与我们联系，我们会在工作时间内尽快回复。',
      },
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');

    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = fd.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送！感谢您的反馈。');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    try {
      const payload = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        'company_alt': '',
      }).toString();

      const res = await fetch('https://readdy.ai/api/form/d9otapiftmtnfcfh0dlg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload,
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送！感谢您的反馈，我们会尽快回复。');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '发送失败，请稍后重试' : serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <title>关于我们_韩国漫画网_免费韩漫大全平台介绍</title>
      <meta name="description" content="韩国漫画网关于我们页面，了解平台的品牌故事、核心优势、用户口碑和联系方式。免费韩漫大全，提供好看韩国漫画在线观看。" />
      <meta name="keywords" content="韩国漫画网,关于我们,免费韩漫大全,韩漫平台,韩漫在线观看" />
      <link rel="canonical" href="https://koreancomics.cn/about" />
      <meta property="og:title" content="关于我们 - 韩国漫画网" />
      <meta property="og:description" content="韩国漫画网关于我们页面，了解平台的品牌故事、核心优势、用户口碑和联系方式。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://koreancomics.cn/about" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="关于我们 - 韩国漫画网" />
      <meta name="twitter:description" content="韩国漫画网关于我们页面，了解平台的品牌故事、核心优势、用户口碑和联系方式。" />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />
      <main className="pt-24 pb-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              关于韩国漫画网
            </h1>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-4xl">
              韩国漫画网成立于2020年，是国内领先的<strong>免费韩漫大全</strong>在线阅读平台。我们致力于为广大韩漫爱好者提供最全面、最优质的<strong>好看韩国漫画</strong>资源，通过便捷的<strong>漫画网址</strong>即可访问，支持<strong>韩漫在线观看</strong>，让每一位读者都能享受流畅的阅读体验。
            </p>
          </section>

          <section className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              <a href="#brand-story">品牌故事</a>
            </h2>
            <div className="mt-6 p-6 md:p-8 bg-background-100 rounded-xl border border-background-200/70">
              <p className="text-sm text-foreground-700 leading-relaxed">
                韩国漫画网的创立源于创始团队对韩国漫画的热爱。我们注意到国内缺乏一个专注于韩国漫画的优质阅读平台，大量优秀的韩漫作品因为语言和渠道的限制无法被中国读者看到。于是我们组建了专业的翻译和运营团队，从零开始搭建了韩国漫画网。
              </p>
              <p className="mt-4 text-sm text-foreground-700 leading-relaxed">
                经过多年的发展，韩国漫画网已经积累了超过一万部韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑等八大分类，月活跃用户突破500万。我们始终坚持以读者为中心，不断优化阅读体验和内容品质。
              </p>
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              <a href="#core-values">核心优势</a>
            </h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: 'ri-book-open-line', title: '海量资源', desc: '超过10,000部免费韩漫资源，涵盖八大热门分类，满足不同读者的阅读偏好。' },
                { icon: 'ri-timer-line', title: '每日更新', desc: '稳定的更新频率，确保读者每天都能看到最新的韩漫章节内容。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美支持PC端和移动端访问，随时随地畅享韩漫在线观看体验。' },
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '严格的内容审核机制，为读者提供安全、健康的阅读环境。' },
              ].map((item) => (
                <div key={item.title} className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-foreground-900">{item.title}</h3>
                  <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              <a href="#user-reviews">用户口碑</a>
            </h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: '小王',
                  role: '韩漫爱好者',
                  avatar: 'https://readdy.ai/api/search-image?query=Korean%20anime%20style%20young%20male%20avatar%20portrait%20simple%20clean%20background%20soft%20lighting&width=80&height=80&seq=avatar-01&orientation=squarish',
                  review: '韩国漫画网真的是我见过最好的韩漫平台！分类清晰，更新速度快，而且全部免费。已经推荐给身边所有喜欢看韩漫的朋友了。',
                  rating: 5,
                },
                {
                  name: '小李',
                  role: '二次元爱好者',
                  avatar: 'https://readdy.ai/api/search-image?query=Korean%20anime%20style%20young%20female%20avatar%20portrait%20simple%20clean%20background%20soft%20lighting&width=80&height=80&seq=avatar-02&orientation=squarish',
                  review: '在这里找到了很多好看的韩国漫画，画风精致，翻译质量也很高。特别是恋爱和奇幻分类，每个作品都很精彩。',
                  rating: 5,
                },
                {
                  name: '老张',
                  role: '资深漫画读者',
                  avatar: 'https://readdy.ai/api/search-image?query=Korean%20anime%20style%20middle%20aged%20male%20avatar%20portrait%20simple%20clean%20background%20soft%20lighting&width=80&height=80&seq=avatar-03&orientation=squarish',
                  review: '作为一名看了十几年漫画的老读者，韩国漫画网的韩漫资源数量和质量都让我惊艳。手机端浏览体验也很流畅。',
                  rating: 5,
                },
              ].map((review) => (
                <div key={review.name} className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-0.5">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-amber-400"></i>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-foreground-600 leading-relaxed">{review.review}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              <a href="#contact">联系我们</a>
            </h2>
            <div className="mt-6 flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@koreancomics.cn' },
                    { icon: 'ri-qq-line', title: '客服QQ', value: '1234567890' },
                    { icon: 'ri-time-line', title: '工作时间', value: '周一至周五 9:00 - 18:00' },
                    { icon: 'ri-wechat-line', title: '微信公众号', value: '韩国漫画网' },
                  ].map((item) => (
                    <div key={item.title} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                        <i className={`${item.icon}`}></i>
                      </div>
                      <div className="mt-3 text-xs text-foreground-500">{item.title}</div>
                      <div className="mt-1 text-sm font-medium text-foreground-900">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                <div className="p-6 bg-background-100 rounded-xl border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-900">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="" className="mt-4 space-y-3">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    />
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="主题"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="请输入您的消息（最多500字）"
                      maxLength={500}
                      rows={4}
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-all resize-none"
                    ></textarea>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="website-alt-field"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="whitespace-nowrap w-full py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 disabled:opacity-60 transition-all duration-200"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                  {formStatus === 'success' && (
                    <p className="mt-3 text-sm text-green-600">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-3 text-sm text-red-500">{formMessage}</p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}